import { useEffect, useState, useRef, useCallback } from "react";
import "components/Screens/Marketplace/Customer/common/ProfileTabs/ProfileTabs.scss";

interface TabItem {
  id: string;
  label: string;
}

interface ProfileTabsProps {
  className?: string;
  tabs?: TabItem[];
}

const DEFAULT_TABS: TabItem[] = [
  { id: "bio", label: "Bio" },
  { id: "overview", label: "Overview" },
  { id: "hands-on", label: "Skills" },
  { id: "software", label: "Software skills" },
  { id: "certifications", label: "Certifications" },
  { id: "work", label: "Work experience" },
  { id: "education", label: "Education" },
];

function ProfileTabs({ className = "", tabs = DEFAULT_TABS }: ProfileTabsProps) {
  const [activeTab, setActiveTab] = useState("bio");
  const observerRef = useRef<IntersectionObserver | null>(null);
  const setupAttemptsRef = useRef(0);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);

  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgrammaticScrollRef = useRef(false);
  const lastActiveTabRef = useRef("bio");
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleHashChange = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const hash = window.location.hash.slice(1);
      if (hash && tabs.some((tab) => tab.id === hash)) {
        setActiveTab(hash);
        lastActiveTabRef.current = hash;
        if (document.referrer !== window.location.href) {
          isProgrammaticScrollRef.current = true;
          scrollToSection(hash);
          setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 1000);
        }
      }
    };

    const initialHash = window.location.hash.slice(1);
    if (initialHash && tabs.some((tab) => tab.id === initialHash)) {
      setActiveTab(initialHash);
      lastActiveTabRef.current = initialHash;
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const setupObserver = () => {
      const success = setupIntersectionObserver();
      if (!success && setupAttemptsRef.current < 5) {
        setupAttemptsRef.current += 1;
        setTimeout(setupObserver, 200 * setupAttemptsRef.current);
      }
    };

    const timer = setTimeout(setupObserver, 100);
    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (activeTab) {
          scrollTabIntoView(activeTab);
        }
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [activeTab]);

  const setupIntersectionObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null,
      rootMargin: "-240px 0px -50% 0px", // Top offset accounts for sticky header (190px + 50px buffer)
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          if (tabs.some((tab) => tab.id === sectionId) && sectionId !== lastActiveTabRef.current) {
            if (debounceTimeoutRef.current) {
              clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(() => {
              if (!isProgrammaticScrollRef.current) {
                setActiveTab(sectionId);
                lastActiveTabRef.current = sectionId;

                scrollTabIntoView(sectionId);

                if (window.location.hash !== `#${sectionId}`) {
                  isProgrammaticScrollRef.current = true;
                  window.history.replaceState(null, "", `#${sectionId}`);
                  setTimeout(() => {
                    isProgrammaticScrollRef.current = false;
                  }, 100);
                }
              }
            }, 100);
          }
        }
      });
    }, options);

    let allSectionsFound = true;
    tabs.forEach((tab) => {
      const element = document.getElementById(tab.id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      } else {
        allSectionsFound = false;
      }
    });

    return allSectionsFound;
  };

  const scrollTabIntoView = (tabId: string) => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const activeTabButton = container.querySelector(`[data-tab-id="${tabId}"]`) as HTMLButtonElement;

      if (activeTabButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeTabButton.getBoundingClientRect();

        // eslint-disable-next-line prefer-destructuring
        const scrollLeft = container.scrollLeft;
        const buttonLeft = buttonRect.left - containerRect.left + scrollLeft;
        const buttonWidth = buttonRect.width;
        const containerWidth = containerRect.width;

        const targetScrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2;

        container.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: "smooth",
        });
      }
    }
  };

  const scrollToSection = useCallback((sectionId: string) => {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      isScrollingRef.current = true;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Use the browser's native scrollIntoView with the scroll-margin-top CSS
      // The sections already have scroll-margin-top: 2rem defined in CSS
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    } else {
      isScrollingRef.current = false;
    }
  }, []);

  const handleTabClick = (tabId: string) => {
    if (isScrollingRef.current) {
      return;
    }

    setActiveTab(tabId);
    lastActiveTabRef.current = tabId;
    isProgrammaticScrollRef.current = true;

    window.location.hash = tabId;
    scrollToSection(tabId);

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 1200);
  };

  return (
    <div className={`profile-tabs ${className}`}>
      <div className="profile-tabs__nav" ref={tabsContainerRef}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-tab-id={tab.id}
            className={`profile-tabs__tab ${activeTab === tab.id ? "profile-tabs__tab--active" : ""}`}
            onClick={() => handleTabClick(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default ProfileTabs;
